import React, { useEffect, useState } from "react";
import { Sparkles, Sun, Sunrise, Moon, Heart, Flame, Wind, Droplets, Mountain, Waypoints } from "lucide-react";
import client from "../lib/api";

const ELEMENT_ICON = {
  "Fogo":  Flame,
  "Ar":    Wind,
  "Água":  Droplets,
  "Terra": Mountain,
};

const ELEMENT_HUE = {
  "Fogo":  "from-[#E37B7B]/20 to-transparent text-[#E37B7B]",
  "Ar":    "from-sky-400/20 to-transparent text-sky-300",
  "Água":  "from-blue-400/20 to-transparent text-blue-300",
  "Terra": "from-emerald-400/20 to-transparent text-emerald-300",
};

const StatPill = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-[#0C0E12] border border-white/5 rounded-2xl p-4 flex flex-col">
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#A29F9B] mb-2">
      <Icon className="w-3 h-3 text-[#C5A880]" /> {label}
    </span>
    <span className="font-display text-xl text-[#F5F2EE] leading-tight">{value}</span>
    {sub && <span className="text-xs text-[#A29F9B] mt-1">{sub}</span>}
  </div>
);

// Friendly meaning of each celestial body / element — displayed in the
// "O que significa?" expandable so users understand their reading.
const BODY_MEANINGS = {
  sun:        { label: "Sol",        text: "Núcleo da identidade, ego e propósito de vida. Define como te projetas e o que te faz brilhar." },
  moon:       { label: "Lua",        text: "Mundo emocional, intuição e necessidades íntimas. Em sinastria romântica é o corpo mais importante." },
  venus:      { label: "Vénus",      text: "Como amas e como esperas ser amado/a. Estética, afeto, prazer e linguagem do romance." },
  mars:       { label: "Marte",      text: "Energia, paixão, desejo e estilo de iniciativa. Como abordas conflito e atracção física." },
  ascendant:  { label: "Ascendente", text: "Máscara social — primeira impressão e estilo natural. Requer hora e cidade de nascimento." },
  balinese:   { label: "Wuku (Bali)", text: "Astrologia balinesa Pawukon — uma 'semana' de 7 dias dentro de 30 nomes míticos. Saptawara é o dia dos 7 dias, Pancawara o dia dos 5 dias. Dá-nos uma ressonância arquetípica complementar à ocidental." },
};

const ELEMENT_MEANINGS = {
  "Fogo":  "Espontâneo, apaixonado, líder. Procura aventura e expressão.",
  "Terra": "Prático, sensorial, fiável. Valoriza segurança e construção.",
  "Ar":    "Mental, comunicativo, social. Vive de ideias e ligação.",
  "Água":  "Emocional, intuitivo, profundo. Liga-se pelo sentir.",
};

/** Compat panel: multi-planet breakdown + balinese summary. Extracted to keep
 *  AstroSection's main render readable. */
const AstroCompatPanel = ({ compat }) => (
  <div className="bg-[#0C0E12] border border-[#C5A880]/20 rounded-2xl p-5">
    <span className="label-uppercase">Compatibilidade astral</span>

    {compat.natal?.breakdown && Object.keys(compat.natal.breakdown).length > 0 && (
      <div className="mt-3 space-y-2">
        {[
          ["moon",      "Lua"],
          ["venus",     "Vénus"],
          ["sun",       "Sol"],
          ["mars",      "Marte"],
          ["ascendant", "Ascendente"],
        ].filter(([k]) => compat.natal.breakdown[k]).map(([k, label]) => {
          const b = compat.natal.breakdown[k];
          return (
            <div key={k} className="flex items-center gap-3 text-sm" data-testid={`natal-${k}`}>
              <span className="text-xs text-[#A29F9B] w-24 shrink-0">{label}</span>
              <span className="text-[#DCD7D2] text-xs min-w-[140px]">
                {b.a} <span className="text-[#A29F9B]">↔</span> {b.b}
              </span>
              <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-[#C5A880]" style={{ width: `${b.score}%` }} />
              </div>
              <span className="text-xs gold-text shrink-0 tabular-nums w-12 text-right">{b.score}%</span>
            </div>
          );
        })}
        <p className="text-[10px] text-[#A29F9B]/70 italic mt-2">
          Pesos: Lua 30% · Vénus 25% · Sol 20% · Marte 15% · Ascendente 10%.
        </p>
      </div>
    )}

    <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm border-t border-white/5 pt-4">
      <div>
        <div className="text-[#A29F9B] text-xs mb-1">Astrologia balinesa</div>
        <p className="text-[#DCD7D2]">
          <strong className="text-[#C5A880]">{compat.balinese.score}%</strong>
          {" — "}{compat.balinese.wuku_a} encontra {compat.balinese.wuku_b}.
          {" "}Pancawara: {compat.balinese.pancawara?.a} ↔ {compat.balinese.pancawara?.b}.
        </p>
      </div>
      {compat.natal?.score != null && (
        <div>
          <div className="text-[#A29F9B] text-xs mb-1">Carta natal (multi-planeta)</div>
          <p className="text-[#DCD7D2]">
            <strong className="text-[#C5A880]">{compat.natal.score}%</strong>
            {" — "}leitura ponderada de cinco corpos celestes.
          </p>
        </div>
      )}
    </div>
  </div>
);

/** Expandable "What does each celestial body mean?" block. Pure render. */
const AstroMeaningsAccordion = ({ astro, sun }) => (
  <details className="mb-6 group" data-testid="astro-meanings">
    <summary className="cursor-pointer text-sm text-[#C5A880] hover:text-[#E8B86F] inline-flex items-center gap-1.5 list-none">
      <Sparkles className="w-3.5 h-3.5" />
      O que significa cada elemento?
      <span className="text-[#A29F9B] text-xs ml-1 group-open:hidden">— clica para abrir</span>
    </summary>
    <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs">
      {[
        ["sun", sun?.name],
        ["moon", astro.moon?.name],
        ["venus", astro.venus?.name],
        ["mars", astro.mars?.name],
        astro.ascendant ? ["ascendant", astro.ascendant.name] : null,
        ["balinese", astro.balinese?.wuku],
      ].filter(Boolean).map(([key, value]) => (
        <div key={key} className="bg-[#0C0E12] border border-white/5 rounded-lg p-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <strong className="text-[#C5A880]">{BODY_MEANINGS[key].label}</strong>
            {value && <span className="text-[#DCD7D2] font-medium">{value}</span>}
          </div>
          <p className="text-[#A29F9B] leading-relaxed">{BODY_MEANINGS[key].text}</p>
        </div>
      ))}
      {sun?.element && (
        <div className="sm:col-span-2 bg-[#0C0E12] border border-[#C5A880]/20 rounded-lg p-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <strong className="text-[#C5A880]">Elemento solar</strong>
            <span className="text-[#DCD7D2] font-medium">{sun.element}</span>
          </div>
          <p className="text-[#A29F9B] leading-relaxed">{ELEMENT_MEANINGS[sun.element]}</p>
        </div>
      )}
    </div>
  </details>
);

/**
 * Small "Cosmologia" card on every profile page.
 *
 * - Self-view: shows the user's own western sun + ascendant (when known) +
 *   Balinese Pawukon reading.
 * - Other profile: same data for the target + a compatibility band that
 *   blends western elements with Balinese pancawara/triwara harmony.
 *
 * Hidden gracefully when neither user has filled their birth date — never
 * pushes a "go fill astrology" CTA to keep it optional.
 */
const AstroSection = ({ targetUserId, isSelf = false }) => {
  const [astro, setAstro] = useState(null);
  const [compat, setCompat] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const tasks = [client.get(`/astro/profile/${targetUserId}`)];
        if (!isSelf) tasks.push(client.get(`/astro/compatibility/${targetUserId}`));
        const [aRes, cRes] = await Promise.all(tasks);
        if (!alive) return;
        setAstro(aRes.data);
        if (cRes) setCompat(cRes.data);
      } catch (err) {
        // Astrology data is optional/decorative. Render nothing on failure
        // (the early returns below handle this) but log for debugging.
        // eslint-disable-next-line no-console
        console.warn("Failed to fetch astro data:", err);
      }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [targetUserId, isSelf]);

  if (loading) return null;
  if (!astro || !astro.has_data) return null;

  const sun = astro.sun;
  const SunElIcon = ELEMENT_ICON[sun?.element] || Sparkles;
  const elTone = ELEMENT_HUE[sun?.element] || "";

  return (
    <section className="mb-12 bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8" data-testid="astro-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <span className="label-uppercase">Cosmologia</span>
          <h2 className="font-display text-2xl md:text-3xl mt-2 inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C5A880]" />
            Leitura astral & balinesa
          </h2>
        </div>
        {compat?.has_data && (
          <div className={`bg-gradient-to-br ${elTone} border border-[#C5A880]/30 rounded-xl px-4 py-2 text-right`} data-testid="astro-compat-score">
            <div className="text-[10px] uppercase tracking-wider text-[#A29F9B]">Afinidade astral</div>
            <div className="font-display text-2xl text-[#C5A880]">{compat.overall}%</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatPill icon={Sun} label="Sol" value={sun?.name || "—"} sub={sun?.element} />
        <StatPill icon={Moon} label="Lua" value={astro.moon?.name || "—"}
          sub={astro.moon ? `${astro.moon.element}` : (isSelf ? "Adiciona hora" : "Sem hora")} />
        <StatPill icon={Heart} label="Vénus" value={astro.venus?.name || "—"} sub={astro.venus?.element} />
        <StatPill icon={Flame} label="Marte" value={astro.mars?.name || "—"} sub={astro.mars?.element} />
        {astro.ascendant_known && astro.ascendant ? (
          <StatPill icon={Sunrise} label="Ascendente" value={astro.ascendant.name}
            sub={`${astro.ascendant.element} · ${astro.ascendant.ruler}`} />
        ) : (
          <StatPill icon={Sunrise} label="Ascendente" value="—"
            sub={isSelf ? "Adiciona hora + cidade" : "Sem hora de nascimento"} />
        )}
        <StatPill icon={Waypoints} label="Wuku (Bali)" value={astro.balinese?.wuku || "—"}
          sub={`${astro.balinese?.saptawara || ""} · ${astro.balinese?.pancawara || ""}`} />
      </div>

      {/* Expandable explanation of what each body / element means */}
      <AstroMeaningsAccordion astro={astro} sun={sun} />

      {astro.balinese?.wuku_meaning_pt && (
        <div className="bg-[#0C0E12] border border-white/5 rounded-2xl p-5 mb-4" data-testid="astro-balinese-text">
          <span className="label-uppercase">Pawukon · {astro.balinese.wuku} (semana {astro.balinese.wuku_index})</span>
          <p className="text-[#DCD7D2] leading-relaxed mt-2">
            {astro.balinese.wuku_meaning_pt}
          </p>
          {astro.balinese.pancawara_meaning_pt && (
            <p className="text-sm text-[#A29F9B] mt-3">
              <strong className="text-[#DCD7D2]">{astro.balinese.pancawara}</strong> — {astro.balinese.pancawara_meaning_pt}
            </p>
          )}
        </div>
      )}

      {compat?.has_data && <AstroCompatPanel compat={compat} />}

      {!isSelf && compat?.has_data === false && (
        <p className="text-xs text-[#A29F9B]/70 mt-4">
          {compat.missing_self
            ? "Adiciona a tua data de nascimento no Perfil para veres a compatibilidade."
            : "Esta pessoa ainda não preencheu a data de nascimento."}
        </p>
      )}

      <p className="text-[10px] text-[#A29F9B]/60 mt-4 italic">
        Leitura energética — não substitui a análise IA do par. Ascendente requer hora + cidade.
        Pawukon: cálculo relativo do ciclo de 210 dias.
      </p>
    </section>
  );
};

export default AstroSection;
